import React, { useEffect, useRef, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'
import pick from 'lodash/pick'

import { dateFormatter, constants } from 'helpers'
import { APPLICATION_STATUS_ACTIONS } from 'constants/application-status'

import Button from 'components/Button'
import Spinner from 'components/Spinner'
import ApplicationFilters from 'pages/admin/AdminWaitlist/ApplicationFilters'

import AVATAR from 'assets/icons/profile_placeholder.png'
import DecisionOptions from 'pages/admin/AdminWaitlist/DecisionOptions'
import SearchInput from 'pages/admin/AdminWaitlist/SearchInput'

const {
  MEMBERSHIP_TYPES,
  H_NUMBERS,
  WAITLIST_STATUS_CONFIG,
  WAITLIST_STATUS,
  ROUTES,
  MEMBERSHIP_VALUES,
} = constants

const TableView = ({
  users,
  onChange,
  payload,
  submit,
  onNext,
  onPrevious,
  currentPage,
  status,
  isFetching,
  onSearch,
  onFilter,
  onSeeDetails,
}) => {
  const itemEls = useRef([])

  const hidden = status !== WAITLIST_STATUS.PRE_APPROVED
  const [data, setData] = useState([])

  useEffect(() => {
    setData(users.data)
  }, [users])

  const toggleStates = useRef({
    membershipType: false,
    referrer: false,
    cardSubmitted: false,
    applicationStatus: false,
    createdAt: false,
  })

  const handleSort = fieldName => {
    const tmp = [...data]
    toggleStates.current[fieldName] = !toggleStates.current[fieldName]
    tmp.sort((a, b) => {
      let element1
      let element2

      if (fieldName === 'referrer') {
        element1 = a.referrer?.name || ''
        element2 = b.referrer?.name || ''
      } else if (fieldName === 'applicationStatus') {
        element1 = a.applicationStatuses.every(aps => aps.completed === true) ? 1 : 0
        element2 = b.applicationStatuses.every(aps => aps.completed === true) ? 1 : 0
      } else if (fieldName === 'membershipType') {
        element1 = a[fieldName] === MEMBERSHIP_TYPES.FOUNDING_MEMBER ? 1 : 0
        element2 = b[fieldName] === MEMBERSHIP_TYPES.FOUNDING_MEMBER ? 1 : 0
      } else if (fieldName === 'cardSubmitted') {
        element1 =
          a.applicationStatus.action === APPLICATION_STATUS_ACTIONS.SAVE_PAYMENT &&
          a.applicationStatus.completed
            ? 1
            : 0
        element2 =
          b.applicationStatus.action === APPLICATION_STATUS_ACTIONS.SAVE_PAYMENT &&
          b.applicationStatus.completed
            ? 1
            : 0
      } else {
        element1 = a[fieldName]
        element2 = b[fieldName]
      }

      if (toggleStates.current[fieldName]) {
        if (element1 < element2) {
          return -1
        }
        if (element1 > element2) {
          return 1
        }
      } else {
        if (element1 < element2) {
          return 1
        }
        if (element1 > element2) {
          return -1
        }
      }

      return 0
    })
    setData(tmp)
  }

  const selectAll = e => {
    const { checked } = e.target

    if (checked) {
      itemEls.current.forEach(item => {
        if (!item) {
          return
        }

        if (!item.checked) {
          item.click()
        }
        item.setAttribute('checked', 'checked')
      })
    } else {
      itemEls.current.forEach(item => {
        if (!item) {
          return
        }

        if (item.checked) {
          item.click()
        }
        item.removeAttribute('checked')
      })
    }
  }

  const getAdditionalOptionsForPreApproved = () => {
    return (
      <div className="flex items-center">
        <Button
          className="flex items-center mr-4 h-9"
          style={{
            background: `linear-gradient(177.65deg, #FFFFFF 1.96%, #C0CEFF 98.03%),linear-gradient(0deg, #FFFFFF, #FFFFFF),linear-gradient(0deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15))`,
          }}
          onClick={() => {
            onChange({ target: { name: 'decision', value: WAITLIST_STATUS.YES } })
            setTimeout(() => {
              submit()
            }, 600)
          }}
          disabled={!payload.h_num}
        >
          <span className="text-sm text-black">Approve Selected</span>
        </Button>
        <div className="flex items-center mr-4">
          <span className="mr-2 text-white text-lg">H#</span>
          <select
            value={payload.h_num}
            onChange={onChange}
            name="h_num"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-100 p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          >
            <option checked={!payload.h_num ? 'checked' : null} value={0}>
              Select
            </option>
            {H_NUMBERS.map(v => (
              <option value={v} key={v} checked={payload.h_num === v ? 'checked' : null}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>
    )
  }

  const creditCardSubmitted = appStatuses => {
    const savePayment = appStatuses.find(
      el => el.action === APPLICATION_STATUS_ACTIONS.SAVE_PAYMENT && el.completed
    )
    return savePayment
  }

  return (
    <>
      <div className="relative">
        <div
          className={`flex justify-between ${
            !hidden || status !== WAITLIST_STATUS.YES ? '' : 'justify-end'
          } mb-5`}
        >
          {hidden ? (
            <DecisionOptions
              onChange={onChange}
              status={status}
              payload={payload}
              onSubmit={submit}
            />
          ) : (
            getAdditionalOptionsForPreApproved()
          )}
          <ApplicationFilters onFilter={onFilter} activeFilters={payload} />
          <div className="flex flex-col items-center">
            <div className="mb-2">
              <SearchInput
                onSearch={(_, location) => onFilter({ location })}
                id="location-search"
                label="Location Search"
              />
            </div>
            <SearchInput onSearch={onSearch} id="waitlist-search" label="Data Search" />
          </div>
        </div>
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="p-4">
                  <div className="flex items-center">
                    <input
                      onChange={e => selectAll(e, itemEls)}
                      id="checkbox-all"
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label htmlFor="checkbox-all" className="sr-only">
                      checkbox
                    </label>
                  </div>
                </th>
                <th scope="col" className="px-3 py-3">
                  ID
                </th>
                <th scope="col" className="px-6 py-3">
                  Name
                </th>
                <th scope="col" className="px-6 py-3">
                  Email
                </th>
                <th scope="col" className="px-6 py-3">
                  Priority
                </th>
                <th scope="col" className="px-6 py-3">
                  Age
                </th>
                <th scope="col" className="px-6 py-3">
                  Gender
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">
                    Membership Type
                    <a href="#/" onClick={() => handleSort('membershipType')}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3 ml-1"
                        aria-hidden="true"
                        fill="currentColor"
                        viewBox="0 0 320 512"
                      >
                        <path d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                      </svg>
                    </a>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">
                    Referred by
                    <a href="#/" onClick={() => handleSort('referrer')}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3 ml-1"
                        aria-hidden="true"
                        fill="currentColor"
                        viewBox="0 0 320 512"
                      >
                        <path d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                      </svg>
                    </a>
                  </div>
                </th>

                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">
                    Credit Card Submitted
                    <a href="#/" onClick={() => handleSort('cardSubmitted')}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3 ml-1"
                        aria-hidden="true"
                        fill="currentColor"
                        viewBox="0 0 320 512"
                      >
                        <path d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                      </svg>
                    </a>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">
                    Application Submitted
                    <a href="#/" onClick={() => handleSort('applicationStatus')}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-3 h-3 ml-1"
                        aria-hidden="true"
                        fill="currentColor"
                        viewBox="0 0 320 512"
                      >
                        <path d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                      </svg>
                    </a>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3">
                  Country
                </th>
                <th scope="col" className="px-6 py-3">
                  Region
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">Waitlist Status</div>
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">Waitlist</div>
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">hNum</div>
                </th>
                <th scope="col" className="px-6 py-3">
                  <div className="flex items-center">Invite Code</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isFetching ? (
                <>
                  {data.map((user, i) => (
                    <tr
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      key={user.id}
                    >
                      <td className="w-4 p-4">
                        <div className="flex items-center">
                          <input
                            id={`checkbox-table-search-${user.id}`}
                            ref={element => itemEls.current.push(element)}
                            type="checkbox"
                            name="id"
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            value={user.id}
                            onChange={onChange}
                          />
                          <label htmlFor={`checkbox-table-search-${user.id}`} className="sr-only">
                            checkbox
                          </label>
                        </div>
                      </td>

                      <td className="px-3 py-4">
                        <Link
                          to={generatePath(ROUTES.ADMIN_USER, { id: user.username || user.id })}
                          target="_blank"
                          className="flex items-center"
                        >
                          {user.id}
                        </Link>
                      </td>
                      <th
                        scope="row"
                        className="flex items-center px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        <img
                          className="w-10 h-10 rounded-full"
                          style={{ background: '#141414' }}
                          src={user.profileImage || AVATAR}
                          alt="My profile avatar"
                        />
                        <div className="pl-2">
                          <a className="text-base" href="#/" onClick={() => onSeeDetails(user)}>
                            {user.name}
                          </a>
                        </div>
                      </th>
                      <td className="px-6 py-4">{user.email || ''}</td>
                      <td className="px-6 py-4">{user.waitlistPriority}</td>
                      <td className="px-6 py-4">
                        {dateFormatter.getAgeFromBirthdate(user.birthdate)}
                      </td>
                      <td className="px-6 py-4">{user.gender}</td>
                      <td className="px-6 py-4">
                        {MEMBERSHIP_VALUES[user?.membershipType] || user?.membershipType}
                      </td>
                      <td className="px-6 py-4">{user?.referrer?.name}</td>

                      <td className="px-6 py-4">
                        {creditCardSubmitted(user.applicationStatuses) ? 'Yes' : 'No'}
                      </td>
                      <td className="px-6 py-4">
                        {user.applicationStatuses.every(aps => aps.completed) ? 'Yes' : 'No'}
                      </td>
                      <td className="px-2 py-4 text-center">{user.currentLocation?.country}</td>
                      <td className="px-2 py-4 text-center">
                        {user.currentLocation
                          ? Object.values(
                              pick(user.currentLocation || {}, ['locality', 'region'])
                            ).join(', ')
                          : user.location}
                      </td>
                      <td className="px-6 py-4">
                        {WAITLIST_STATUS_CONFIG[user.waitlistStatus]?.state}
                      </td>
                      <td className="px-6 py-4">{user.waitlist ? 'On waitlist' : 'App Access'}</td>
                      <td className="px-6 py-4">{user.hNum}</td>
                      <td className="px-6 py-4">{user.inviteReferralCode}</td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr className="w-full bg-white">
                  <td className="w-full bg-white" colSpan={100}>
                    <div
                      className="w-full flex items-center justify-center h-60"
                      style={{
                        maxWidth: '100vw',
                      }}
                    >
                      <Spinner />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <br />
        {users.count > users.limit && (
          <div className="flex flex-col items-center">
            <span className="text-sm text-white">
              Showing <span className="font-semibold text-white">{currentPage}</span> {`to `}
              <span className="font-semibold  text-white">{users.pages}</span>
              {` of`}
              <span className="font-semibold  text-white">{users.count}</span>
              Entries
            </span>
            <div className="inline-flex mt-2 xs:mt-0">
              <button
                onClick={onPrevious}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-l hover:bg-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Prev
              </button>
              <button
                onClick={onNext}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-800 border-0 border-l border-gray-700 rounded-r hover:bg-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              >
                Next
                <svg
                  aria-hidden="true"
                  className="w-5 h-5 ml-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default TableView
